import { useState } from "react";
import { Loader2, LogOut, Plus, ChevronDown, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cognitoLogout } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { useSidebar } from "@/components/ui/sidebar";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useSwitchRole } from "@/hooks/use-switch-role";
import { ROLE_DISPLAY, otherRole, initialsFrom } from "@/lib/roleDisplay";
import type { Role } from "@/services/UserManagmentServices";

interface AccountSwitcherProps {
  /** The role the surrounding dashboard is rendering for. */
  currentRole: Role;
  /** Display name (falls back to the profile name / email). */
  name?: string;
  /** Avatar image URL (falls back to initials). */
  avatar?: string;
}

const ALL_ROLES: Role[] = ["TEACHER", "STUDENT"];

export function AccountSwitcher({ currentRole, name, avatar }: AccountSwitcherProps) {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { user, isLoading: userLoading } = useCurrentUser();
  const { acquireRole, pending } = useSwitchRole();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const cur = ROLE_DISPLAY[currentRole];
  const target = otherRole(currentRole);
  const displayName = (name && name.trim()) || user?.name || user?.email || cur.short;
  const email = user?.email ?? "";
  const initials = initialsFrom(displayName, email);

  const handleRoleClick = (role: Role) => {
    if (role === currentRole) return;
    setDropdownOpen(false);
    const hasRole = user?.roles?.some((r) => r.role === role) ?? false;
    // Always show the switch-role transition page first.
    // Pass acquire=true when the role doesn't exist yet so the page knows
    // to create it and redirect to onboarding instead of the dashboard.
    const params = hasRole ? `to=${role}` : `to=${role}&acquire=true`;
    navigate(`/switch-role?${params}`);
  };

  const AvatarBlock = ({ size }: { size: string }) => (
    <div className="relative shrink-0">
      <Avatar className={cn(size, "rounded-lg")}>
        {avatar ? <AvatarImage src={avatar} alt={displayName} className="rounded-lg object-cover" /> : null}
        <AvatarFallback className="rounded-lg bg-white/15 text-white text-xs font-semibold">
          {initials}
        </AvatarFallback>
      </Avatar>
      <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-white dark:border-gray-950" />
    </div>
  );

  /* ── Collapsed: avatar opens full popover ── */
  if (isCollapsed) {
    return (
      <Popover open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Account menu"
            className="flex justify-center w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg"
          >
            <AvatarBlock size="w-8 h-8" />
          </button>
        </PopoverTrigger>

        <PopoverContent side="bottom" align="start" sideOffset={8} collisionPadding={{ top: 60 }} className="w-[260px] p-1.5 rounded-xl bg-slate-800/95 backdrop-blur-md border-white/10 shadow-xl z-[60]">
          <p className="text-xs font-bold uppercase tracking-widest text-sidebar-foreground/40 px-2.5 pt-1.5 pb-1">
            Switch mode
          </p>

          {ALL_ROLES.map((role) => {
            const cfg = ROLE_DISPLAY[role];
            const isActive = role === currentRole;
            const hasRole = userLoading ? null : (user?.roles?.some((r) => r.role === role) ?? false);
            const isPending = pending === role;

            return (
              <button
                key={role}
                type="button"
                onClick={() => !isActive && hasRole !== null && handleRoleClick(role)}
                disabled={isActive || isPending || hasRole === null}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                  isActive ? "cursor-default bg-white/10" : "hover:bg-white/10",
                  (isPending || hasRole === null) && "opacity-60 cursor-not-allowed"
                )}
              >
                {isPending || (!isActive && hasRole === null)
                  ? <Loader2 className="w-4 h-4 animate-spin text-white/50 shrink-0" />
                  : isActive || hasRole
                    ? <cfg.icon className={cn("w-4 h-4 shrink-0", isActive ? "text-white" : "text-white/50")} />
                    : <Plus className="w-4 h-4 text-white/40 shrink-0" />
                }
                <span className="flex-1 min-w-0 text-left">
                  <span className={cn("block text-[13px] font-medium leading-tight",
                    isActive ? "text-white" : "text-white/70"
                  )}>
                    {cfg.short}
                  </span>
                  {!isActive && hasRole === false && (
                    <span className="block text-xs text-white/40 mt-0.5">Not set up yet</span>
                  )}
                </span>
                {isActive && <Check className="w-4 h-4 text-emerald-400 shrink-0" />}
              </button>
            );
          })}

          <div className="h-px bg-white/10 mx-2 my-1" />

          <button
            type="button"
            onClick={() => cognitoLogout()}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            <span className="text-[13px] font-medium">Sign out</span>
          </button>
        </PopoverContent>
      </Popover>
    );
  }

  /* ── Expanded: name row with chevron → dropdown ── */
  const targetCfg = ROLE_DISPLAY[target];
  const targetHasRole = userLoading ? null : (user?.roles?.some((r) => r.role === target) ?? false);
  const isPending = pending === target;

  return (
    <Popover open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Account menu"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/8 hover:bg-white/15 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <AvatarBlock size="w-9 h-9" />
          <div className="min-w-0 flex-1 text-left">
            <p className="text-[15px] font-semibold text-sidebar-foreground truncate leading-tight">
              {displayName}
            </p>
            <p className="text-xs mt-0.5 text-white/70">
              <span className="text-white/40">Role: </span>
              <span className="font-semibold">{cur.short}</span>
            </p>
          </div>
          <ChevronDown className={cn(
            "w-4 h-4 text-sidebar-foreground/50 shrink-0 transition-transform duration-200",
            dropdownOpen && "rotate-180"
          )} />
        </button>
      </PopoverTrigger>

      <PopoverContent side="bottom" align="start" sideOffset={6} collisionPadding={{ top: 60 }} className="w-[260px] p-1.5 rounded-xl bg-slate-800/95 backdrop-blur-md border-white/10 shadow-xl z-[60]">

        <p className="text-xs font-bold uppercase tracking-widest text-white/40 px-3 pt-1.5 pb-1">
          Switch mode
        </p>

        {/* Active role — non-clickable */}
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/10">
          <cur.icon className="w-4 h-4 text-white shrink-0" />
          <span className="flex-1 min-w-0 text-[13px] font-medium text-white leading-tight">
            {cur.short}
          </span>
          <Check className="w-4 h-4 text-emerald-400 shrink-0" />
        </div>

        {/* Switch-to option */}
        <button
          type="button"
          onClick={() => targetHasRole !== null && handleRoleClick(target)}
          disabled={isPending || targetHasRole === null}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/10 transition-colors",
            (isPending || targetHasRole === null) && "opacity-60 cursor-not-allowed"
          )}
        >
          {isPending || targetHasRole === null
            ? <Loader2 className="w-4 h-4 animate-spin text-white/50 shrink-0" />
            : targetHasRole
              ? <targetCfg.icon className="w-4 h-4 text-white/50 shrink-0" />
              : <Plus className="w-4 h-4 text-white/40 shrink-0" />
          }
          <span className="flex-1 min-w-0 text-left">
            <span className="block text-[13px] font-medium text-white/70 leading-tight">
              {targetCfg.short}
            </span>
            {targetHasRole === false && (
              <span className="block text-xs text-white/40 mt-0.5">Not set up yet</span>
            )}
          </span>
        </button>

        <div className="h-px bg-white/10 mx-2 my-1" />

        {/* Sign out */}
        <button
          type="button"
          onClick={() => cognitoLogout()}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"
        >
          <LogOut className="w-4 h-4 shrink-0" />
          <span className="text-[13px] font-medium">Sign out</span>
        </button>

      </PopoverContent>
    </Popover>
  );
}
